import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, Settings, Check, X } from "lucide-react";
import { http } from "@/lib/api";
import { useI18n } from "@/lib/i18n";

const KEY = "wv_cookie_consent_v1";

/**
 * CookieBanner — kritiek: mag NOOIT een formulier-knop (bv. "Account aanmaken") overlappen.
 * Strategie: standaard geminimaliseerd tot een klein bubbeltje rechtsonder dat niets afdekt.
 * Pas als de gebruiker het bubbeltje aanklikt opent een echte modal (centered overlay).
 * Modal heeft eigen backdrop en blokkeert dus bewust de hele pagina — maar alleen op expliciete gebruikersactie.
 */
export default function CookieBanner() {
  const { t } = useI18n();
  const [stored, setStored] = useState(() => {
    try { return JSON.parse(localStorage.getItem(KEY) || "null"); }
    catch (e) { return null; }
  });
  const [open, setOpen] = useState(false);        // modal open?
  const [detail, setDetail] = useState(false);     // showing categories?
  const [prefs, setPrefs] = useState(stored || { necessary: true, preferences: false, analytics: false, marketing: false });
  const dialogRef = useRef(null);

  const consented = !!stored?.savedAt;

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const CATEGORIES = [
    { key: "necessary",   label: t("cookie.cat_necessary"),   desc: t("cookie.cat_necessary_desc"),   locked: true },
    { key: "preferences", label: t("cookie.cat_preferences"), desc: t("cookie.cat_preferences_desc"), locked: false },
    { key: "analytics",   label: t("cookie.cat_analytics"),   desc: t("cookie.cat_analytics_desc"),   locked: false },
    { key: "marketing",   label: t("cookie.cat_marketing"),   desc: t("cookie.cat_marketing_desc"),   locked: false },
  ];

  const save = async (choice) => {
    const final = { ...choice, savedAt: new Date().toISOString() };
    localStorage.setItem(KEY, JSON.stringify(final));
    setStored(final); setPrefs(final); setOpen(false); setDetail(false);
    try { await http.post("/consent", { ...choice, user_agent: navigator.userAgent }); }
    catch (e) { console.error("consent log failed:", e); }
  };

  const acceptAll = () => save({ necessary: true, preferences: true, analytics: true, marketing: true });
  const rejectAll = () => save({ necessary: true, preferences: false, analytics: false, marketing: false });
  const saveCustom = () => save(prefs);

  // Bubble that opens the modal. Shown ALWAYS so user can revisit their choice.
  // Positioned bottom-right with safe-area padding; `pointer-events: auto` only on the bubble itself.
  const Bubble = (
    <button
      onClick={() => { setOpen(true); setDetail(!!consented); }}
      className="fixed bottom-4 right-4 z-40 bg-white border border-slate-200 shadow-lg rounded-full h-11 w-11 sm:h-auto sm:w-auto sm:px-4 sm:py-2.5 grid place-items-center sm:inline-flex sm:items-center sm:gap-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
      aria-label="Cookie-voorkeuren"
      data-testid="cookie-banner-bubble"
    >
      <Cookie className="w-4 h-4 text-[color:var(--wv-green-dark)]" />
      <span className="hidden sm:inline">{consented ? t("cookie.customize") : t("cookie.title")}</span>
    </button>
  );

  return (
    <>
      {Bubble}
      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-3 sm:p-6"
          onClick={() => setOpen(false)}
          data-testid="cookie-banner-modal"
        >
          <div
            ref={dialogRef}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-xl max-h-[85vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col"
            data-testid="cookie-banner"
          >
            <div className="p-5 sm:p-6 border-b border-slate-100 flex items-start gap-3">
              <div className="w-11 h-11 rounded-xl bg-[color:var(--wv-green)]/10 grid place-items-center flex-shrink-0">
                <Cookie className="w-5 h-5 text-[color:var(--wv-green-dark)]" />
              </div>
              <div className="flex-1">
                <p className="font-display font-bold text-lg text-[color:var(--wv-obsidian)]">{t("cookie.title")}</p>
                <p className="mt-1 text-sm text-slate-600">{t("cookie.desc_prefix")} <Link to="/privacy" className="text-[color:var(--wv-navy)] font-semibold hover:underline">{t("cookie.privacy_link")}</Link>.</p>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 flex-shrink-0"
                aria-label={t("close")}
                data-testid="cookie-banner-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {detail && (
              <div className="flex-1 overflow-y-auto p-5 sm:p-6 space-y-2">
                {CATEGORIES.map((c) => (
                  <label key={c.key} className={`flex items-start gap-3 p-3 rounded-xl border ${prefs[c.key] ? "border-[color:var(--wv-green)]/40 bg-[color:var(--wv-green)]/5" : "border-slate-200"}`}>
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={!!prefs[c.key]}
                      disabled={c.locked}
                      onChange={(e) => setPrefs({ ...prefs, [c.key]: e.target.checked })}
                      data-testid={`cookie-${c.key}`}
                    />
                    <div className="flex-1">
                      <p className="font-semibold text-sm text-slate-900">{c.label} {c.locked && <span className="ml-2 text-[10px] uppercase font-bold text-slate-500">{t("cookie.always")}</span>}</p>
                      <p className="text-xs text-slate-600 mt-0.5">{c.desc}</p>
                    </div>
                  </label>
                ))}
              </div>
            )}
            <div className="p-5 sm:p-6 border-t border-slate-100 flex flex-wrap gap-2 justify-end">
              {!detail ? (
                <button onClick={() => setDetail(true)} className="wv-btn-outline text-sm inline-flex items-center gap-1" data-testid="cookie-customize"><Settings className="w-3.5 h-3.5" /> {t("cookie.customize")}</button>
              ) : (
                <button onClick={saveCustom} className="wv-btn-outline text-sm inline-flex items-center gap-1" data-testid="cookie-save-custom"><Check className="w-3.5 h-3.5" /> {t("cookie.save_choice")}</button>
              )}
              <button onClick={rejectAll} className="wv-btn-outline text-sm" data-testid="cookie-reject-all">{t("cookie.only_necessary")}</button>
              <button onClick={acceptAll} className="wv-btn-green text-sm" data-testid="cookie-accept-all">{t("cookie.accept_all")}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
